import {
  Badge,
  Body1,
  Body2,
  Button,
  Card,
  CardHeader,
  Caption1,
  Link,
  MessageBar,
  MessageBarBody,
  Subtitle1,
  Subtitle2,
  Title2,
} from '@fluentui/react-components';
import {
  ArrowLeft20Regular,
  BookInformation20Regular,
  Building20Filled,
  Building20Regular,
  Calendar20Filled,
  Calendar20Regular,
  Chat20Filled,
  Chat20Regular,
  DocumentMultiple20Filled,
  DocumentMultiple20Regular,
  Open20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useNavigate } from 'react-router';

import {
  OFFICIAL_TENANT_STATUS_URL,
  SYNTHETIC_TENANT_ADVANCED_GUIDANCE_URL,
  SYNTHETIC_TENANT_AUGLOOP_ONBOARDING_URL,
  SYNTHETIC_TENANT_GUIDANCE_URL,
  WRITING_EVALS_SYNTHETIC_TENANTS_URL,
} from '../../constants/index.js';
import { useOfficialEvaluationTenant } from '../../hooks/useOfficialEvaluationTenant/useOfficialEvaluationTenant.js';
import type { OfficialEvaluationTenant } from '../../services/officialEvaluationTenantService.js';

import { useStyles } from './EvaluationTenantsPage.style.js';

const BuildingIcon = bundleIcon(
  Building20Filled,
  Building20Regular,
) as React.FC;
const CalendarIcon = bundleIcon(
  Calendar20Filled,
  Calendar20Regular,
) as React.FC;
const ChatIcon = bundleIcon(Chat20Filled, Chat20Regular) as React.FC;
const DocumentIcon = bundleIcon(
  DocumentMultiple20Filled,
  DocumentMultiple20Regular,
) as React.FC;

const DATA_CAPABILITIES = [
  {
    title: 'Teams collaboration',
    description: 'Teams, channels, chats, channel messages, and participants',
    icon: ChatIcon,
  },
  {
    title: 'Meetings and events',
    description: 'Online meetings, channel-linked transcripts, and calendars',
    icon: CalendarIcon,
  },
  {
    title: 'Microsoft 365 knowledge',
    description: 'Email, files, SharePoint sites, OneNote, and contacts',
    icon: DocumentIcon,
  },
  {
    title: 'Enterprise knowledge',
    description: 'Azure DevOps, Confluence, ServiceNow, and intranet sources',
    icon: BuildingIcon,
  },
] as const;

const RECREATION_STEPS = [
  {
    text: 'Recreate the tenant from the approved evalset; synthetic tenants cannot be renewed.',
    linkLabel: 'Tenant provisioning guide',
    href: `${SYNTHETIC_TENANT_ADVANCED_GUIDANCE_URL}#advanced--creating-a-new-tenant`,
  },
  {
    text: 'Re-run Ring Zero and authentication onboarding for the new physical tenant.',
    linkLabel: 'AugLoop onboarding guide',
    href: SYNTHETIC_TENANT_AUGLOOP_ONBOARDING_URL,
  },
  {
    text: 'Update evaluations that reference tenant, user, team, channel, chat, or meeting IDs.',
    linkLabel: 'Tenant-bound ID guidance',
    href: `${WRITING_EVALS_SYNTHETIC_TENANTS_URL}#step-5--trial-data-is-tenant-bound`,
  },
  {
    text: 'Validate representative retrieval and evaluation scenarios before switching consumers.',
    linkLabel: 'Local validation guide',
    href: `${WRITING_EVALS_SYNTHETIC_TENANTS_URL}#step-6--verify-locally-before-standing-up-a-gate`,
  },
] as const;

export function EvaluationTenantsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { data: tenant } = useOfficialEvaluationTenant();

  return (
    <div className={styles.root}>
      <Button
        appearance="subtle"
        icon={<ArrowLeft20Regular />}
        className={styles.backButton}
        onClick={() => {
          void navigate('/');
        }}
      >
        Back to home
      </Button>

      <header className={styles.header}>
        <Title2>Evaluation tenants</Title2>
        <Body1 className={styles.introduction}>
          Use the official tenant for shared evaluations and CI/CD. Use a
          playground tenant when experiments need isolated or unrestricted data
          changes.
        </Body1>
        <div className={styles.headerActions}>
          <Button
            as="a"
            appearance="primary"
            href={OFFICIAL_TENANT_STATUS_URL}
            target="_blank"
            rel="noreferrer"
            icon={<Open20Regular />}
          >
            View in Seval
          </Button>
          <Button
            as="a"
            appearance="secondary"
            href={SYNTHETIC_TENANT_GUIDANCE_URL}
            target="_blank"
            rel="noreferrer"
            icon={<BookInformation20Regular />}
          >
            Open technical documentation
          </Button>
        </div>
      </header>

      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <Subtitle1>Official evaluation tenant</Subtitle1>
          <Body2 className={styles.sectionDescription}>
            The shared environment for organization-wide evaluations and CI/CD
            gates.
          </Body2>
        </div>
        <Card className={styles.officialCard}>
          <CardHeader
            image={
              <div className={styles.officialIcon}>
                <BuildingIcon />
              </div>
            }
            header={
              <Subtitle2>{tenant?.name ?? 'Teams_EcoSync_V4_Clean'}</Subtitle2>
            }
            description={
              <Caption1 className={styles.label}>
                Teams golden evaluation tenant
              </Caption1>
            }
            action={
              <Badge appearance="tint" color="brand">
                Official
              </Badge>
            }
          />
          <div className={styles.officialDetails}>
            <div className={styles.detailItem}>
              <Caption1 className={styles.label}>Use for</Caption1>
              <Body2>Stable shared evaluations and CI/CD gates</Body2>
            </div>
            <div className={styles.detailItem}>
              <Caption1 className={styles.label}>Data changes</Caption1>
              <Body2>
                Reviewed changes that preserve existing evaluation dependencies
              </Body2>
            </div>
            <div className={styles.detailItem}>
              <Caption1 className={styles.label}>Lifecycle</Caption1>
              <Body2>{formatLifecycle(tenant)}</Body2>
            </div>
          </div>
        </Card>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <Subtitle1>Data available</Subtitle1>
          <Body2 className={styles.sectionDescription}>
            The official tenant is hydrated with synthetic Microsoft 365 and
            enterprise knowledge for end-to-end retrieval evaluations.
          </Body2>
        </div>
        <div className={styles.dataGrid}>
          {DATA_CAPABILITIES.map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.title} className={styles.dataCard}>
                <div className={styles.dataCardHeader}>
                  <div className={styles.dataIcon}>
                    <Icon />
                  </div>
                  <Subtitle2>{item.title}</Subtitle2>
                </div>
                <Body2>{item.description}</Body2>
              </Card>
            );
          })}
        </div>
        <Caption1 className={styles.dataSources}>
          View current counts, ingestion health, and expiration in Seval.
        </Caption1>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <Subtitle1>Choose the right tenant</Subtitle1>
        </div>
        <div className={styles.guidanceGrid}>
          <Card className={styles.guidanceCard}>
            <div className={styles.guidanceHeader}>
              <Subtitle2>Official tenant</Subtitle2>
              <Badge appearance="tint" color="success">
                Shared and stable
              </Badge>
            </div>
            <Body2>
              Use for stable, organization-wide evaluations and CI/CD. Add
              compatible data through reviewed changes without modifying
              entities that existing evaluations depend on.
            </Body2>
          </Card>
          <Card className={styles.guidanceCard}>
            <div className={styles.guidanceHeader}>
              <Subtitle2>Playground tenant</Subtitle2>
              <Badge appearance="tint" color="warning">
                Isolated and temporary
              </Badge>
            </div>
            <Body2>
              Use for unusual experiments, mutations, or unrestricted data
              changes. Playground tenants expire after 90 days and must be
              recreated rather than renewed.
            </Body2>
            <Link
              href={`${SYNTHETIC_TENANT_GUIDANCE_URL}#i-think-i-need-a-different-tenant`}
              target="_blank"
              rel="noreferrer"
            >
              When to use a separate tenant
            </Link>
          </Card>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <Subtitle1>Where data changes belong</Subtitle1>
        </div>
        <div className={styles.rulesGrid}>
          <Card className={styles.ruleCard}>
            <Subtitle2>Yaar team</Subtitle2>
            <Body2>
              Use its allocated teams and channels for flexible additions that
              must not affect existing golden evaluations.
            </Body2>
          </Card>
          <Card className={styles.ruleCard}>
            <Subtitle2>Meeting transcripts</Subtitle2>
            <Body2>
              Transfer channel-linked transcripts into an isolated team in the
              golden tenant so meeting and channel tests do not interfere.
            </Body2>
          </Card>
          <Card className={styles.ruleCard}>
            <Subtitle2>Existing shared entities</Subtitle2>
            <Body2>
              Changes require review because edits to users, teams, channels,
              chats, or meetings can invalidate other evaluations.
            </Body2>
          </Card>
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeading}>
          <Subtitle1>Recreate a playground tenant</Subtitle1>
          <Body2 className={styles.sectionDescription}>
            Playground tenants expire after 90 days and cannot be renewed.
          </Body2>
        </div>
        <Card className={styles.recreationCard}>
          <div className={styles.steps}>
            {RECREATION_STEPS.map((step, index) => (
              <div key={step.text} className={styles.step}>
                <span className={styles.stepNumber}>{index + 1}</span>
                <div className={styles.stepContent}>
                  <Body2>{step.text}</Body2>
                  <Link href={step.href} target="_blank" rel="noreferrer">
                    {step.linkLabel}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </section>

      <MessageBar
        intent="warning"
        layout="multiline"
        className={styles.warningBar}
      >
        <MessageBarBody className={styles.warningBody}>
          Refresh and versioning rules, and final ownership for official-tenant
          changes, are still being defined. Use a playground tenant for changes
          that could alter shared evaluation results.
        </MessageBarBody>
      </MessageBar>
    </div>
  );
}

function formatLifecycle(tenant: OfficialEvaluationTenant | undefined): string {
  if (!tenant) {
    return 'Long-lived main tenant; check Seval for the current expiration';
  }

  const expiration = tenant.expiresAt
    ? new Date(tenant.expiresAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : 'expiration unavailable';

  return `${tenant.provisioningState} · ${tenant.userCount.toLocaleString('en-US')} users · expires ${expiration}`;
}
